import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS, MUTATION_KEYS } from '@avanta/shared/constants/common';
import { TOP_ONLY_SHADOW_CLASS_NAME } from '@avanta/shared/constants/styles';
import { useHasVerticalOverflow } from '@avanta/shared/hooks/useHasVerticalOverflow';
import { cn } from '@avanta/shared/utils/cn';
import { useIsMutating } from '@tanstack/react-query';
import { useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SessionSummary } from 'pages/AppointmentsReserve/components/ui/SessionSummary';
import { StepBackButton } from 'pages/AppointmentsReserve/components/ui/StepBackButton';
import { StepSection } from 'pages/AppointmentsReserve/components/ui/StepSection';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { useGetContractOptions } from 'pages/AppointmentsReserve/hooks/useGetContractOptions';
import { type StepProps, type AppointmentsReserveFormData } from 'pages/AppointmentsReserve/types';
import { areRecordFieldsComplete } from 'pages/AppointmentsReserve/utils';
import { AdministrativeFields } from './components/AdministrativeFields';
import { Documents } from './components/Documents';
import { Observations } from './components/Observations';

export const ConfirmationStep = ({ onBack }: StepProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { control } = useFormContext<AppointmentsReserveFormData>();
  const [contractId, contractRateId, recordFields] = useWatch({
    control,
    name: [
      AppointmentsReservePayload.contractId,
      AppointmentsReservePayload.contractRateId,
      AppointmentsReservePayload.recordFields,
    ],
  });
  const { recordFields: recordFieldDefinitions } = useGetContractOptions();
  const isPendingEdit = useIsMutating({ mutationKey: [MUTATION_KEYS.UPDATE_APPOINTMENT] }) > 0;
  const isPendingBook = useIsMutating({ mutationKey: [MUTATION_KEYS.BOOK_APPOINTMENT] }) > 0;
  const isValid =
    Boolean(contractId) &&
    Boolean(contractRateId) &&
    areRecordFieldsComplete(recordFieldDefinitions, recordFields ?? {});
  const contentRef = useRef<HTMLDivElement>(null);
  const { hasOverflow, isAtBottom } = useHasVerticalOverflow(
    contentRef,
    'nearestScrollParent',
    recordFieldDefinitions
  );
  const showScrollShadow = hasOverflow && !isAtBottom;

  return (
    <div ref={contentRef} className="flex flex-col gap-12 max-w-241 w-full">
      <div className="flex gap-8">
        <StepSection title={t('AppointmentCard.details.heading', { ns: LOCIZE_NS.INSURANCE })}>
          <SessionSummary />
        </StepSection>
        <StepSection containerClassName="mt-8" className="gap-10 flex-col flex">
          <AdministrativeFields />
          <Documents />
          <Observations />
        </StepSection>
      </div>
      <div
        className={cn(
          'sticky bottom-0 z-10 w-full max-w-241 bg-white pt-6',
          showScrollShadow && TOP_ONLY_SHADOW_CLASS_NAME
        )}
      >
        <div className="relative z-20 flex justify-between gap-4">
          <StepBackButton onClick={onBack} />
          <Button
            type="submit"
            size="lg"
            className="max-w-160 w-full"
            disabled={!isValid}
            isLoading={isPendingEdit || isPendingBook}
          >
            {t('AppointmentsReserve.Steps.confirmation.Insurance.onSubmit')}
          </Button>
        </div>
      </div>
    </div>
  );
};
